import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, LayoutGrid, Zap, CalendarDays, Newspaper } from 'lucide-react';
import itemService from '../../services/itemService';
import catalogService from '../../services/catalogService'; 
import { newsService, newsCategoryService } from '../../services/contentService';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';
import SaleGroupSection from '../../components/common/SaleGroupSection';
import { getImageUrl, generateSlug } from '../../utils/helpers';

const HotDealPage = () => {
    const [dealGroups, setDealGroups] = useState([]);
    const [newsList, setNewsList] = useState([]);
    const [adminSpecs, setAdminSpecs] = useState([]);
    const [loadingDeals, setLoadingDeals] = useState(true); 
    const [loadingNews, setLoadingNews] = useState(true);

    useEffect(() => {
        // 👉 Cuộn mượt lên trên cùng một cách ngay lập tức để không lỗi CLS
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, []);

    useEffect(() => {
        const fetchDealsAndSpecs = async () => {
            setLoadingDeals(true);
            try {
                const [dealsRes, specsRes] = await Promise.all([
                    itemService.getHotDeals().catch(() => []), 
                    catalogService.getSpecs().catch(() => [])
                ]);
                
                const deals = Array.isArray(dealsRes) ? dealsRes : (dealsRes?.data || []);
                setDealGroups(deals);

                const specs = Array.isArray(specsRes) ? specsRes : (specsRes?.data || []);
                setAdminSpecs(specs);
                
            } catch (error) {
                console.error("Lỗi lấy dữ liệu Deals:", error);
            } finally {
                setLoadingDeals(false);
            }
        };
        fetchDealsAndSpecs();
    }, []);

    useEffect(() => {
        const fetchNews = async () => {
            setLoadingNews(true);
            try {
                const [newsRes, catRes] = await Promise.all([
                    newsService.getPublicNews(),
                    newsCategoryService.getPublicCategories().catch(() => [])
                ]);

                const allNews = Array.isArray(newsRes?.data) ? newsRes.data : (Array.isArray(newsRes) ? newsRes : []);
                const allCats = Array.isArray(catRes?.data) ? catRes.data : (Array.isArray(catRes) ? catRes : []);

                const promoCategory = allCats.find(c => 
                    c.categoryName && c.categoryName.toLowerCase().trim() === 'khuyến mãi'
                );

                const promoNews = allNews.filter(n => {
                    if (promoCategory) {
                        return n.categoryId === promoCategory.categoryId;
                    }
                    return (n.newsTitle?.toLowerCase().includes('khuyến mãi') || 
                            n.newsExcerpt?.toLowerCase().includes('khuyến mãi'));
                }).slice(0, 4);
                
                setNewsList(promoNews);
            } catch (error) {
                console.error("Lỗi lấy tin tức:", error);
            } finally {
                setLoadingNews(false);
            }
        };
        fetchNews();
    }, []);

    useEffect(() => {
        document.title = "Khuyến mãi Hot Deal - Săn hàng chính hãng giá sốc | Thành Nam PC";
        let metaDescriptionTag = document.querySelector('meta[name="description"]');
        if (metaDescriptionTag) {
            metaDescriptionTag.setAttribute(
                "content", 
                "Tổng hợp các chương trình Hot Deal, giảm giá sốc các mặt hàng linh kiện, máy tính tại Thành Nam PC Quảng Ninh. Săn deal chính hãng, bảo hành uy tín ngay hôm nay!"
            );
        }
    }, []);

    return (
        <div className="flex flex-col min-h-screen bg-gray-50 font-inter">
            <Header />

            {/* 👉 TỐI ƯU 3: Cải thiện hiệu suất load Font, loại bỏ CLS khi tải phông chữ trễ */}
            <style dangerouslySetInnerHTML={{__html: `
                @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@700&display=swap');

                @keyframes gradient-x {
                    0%, 100% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                }
                .animate-gradient-x {
                    background-size: 200% 200%;
                    animation: gradient-x 4s ease infinite;
                }
                
                .tech-font {
                    font-family: 'Oswald', sans-serif;
                }

                @keyframes neon-glow-indigo {
                    0%, 100% { text-shadow: 0 0 4px rgba(49, 46, 129, 0.2); }
                    50% { text-shadow: 0 0 10px rgba(49, 46, 129, 0.5), 0 0 15px rgba(49, 46, 129, 0.3); }
                }
                .animate-neon-indigo {
                    animation: neon-glow-indigo 2s ease-in-out infinite;
                }
            `}} />

            <main className="flex-grow pb-12">
                <div className="animate-gradient-x bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 shadow-md relative overflow-hidden border-b-4 border-yellow-600">
                    <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] mix-blend-overlay"></div>
                    
                    <div className="container mx-auto px-4 py-4 md:py-6 relative z-10 flex items-center justify-start gap-4 md:gap-5 min-h-[96px]">
                        <div className="inline-flex items-center justify-center bg-indigo-900 p-3 rounded-full border-2 border-indigo-800 shadow-[0_4px_15px_rgba(49,46,129,0.5)] shrink-0 animate-pulse">
                            <Zap size={34} className="text-yellow-300 fill-yellow-300 drop-shadow-[0_0_8px_rgba(253,224,71,0.8)]" />
                        </div>
                        
                        <div className="flex flex-col">
                            <div className="relative inline-block">
                                <div className="absolute -inset-x-2 -inset-y-1 bg-white/40 rounded-sm skew-x-[-15deg] border border-white/50 backdrop-blur-[2px]"></div>
                                <h1 className="tech-font relative text-3xl md:text-4xl font-bold uppercase italic tracking-wider text-indigo-900 leading-none animate-neon-indigo">
                                    Hot deal
                                    <span className="text-red-500 animate-ping absolute -right-3 top-0 text-xs">•</span>
                                </h1>
                            </div>
                            
                            <p className="tech-font text-xs md:text-sm font-normal text-indigo-800 italic tracking-wider leading-snug mt-2 uppercase drop-shadow-sm">
                                Các chương trình khuyến mãi đặc biệt đến từ Thành Nam
                            </p>
                        </div>
                    </div>
                </div>

                <div className="container mx-auto px-4 mt-8 max-w-7xl space-y-8 min-h-screen">
                    {/* 👉 TỐI ƯU 4: Cập nhật Skeleton chính xác với khung giao diện SaleGroupSection (Tránh giật layout 100%) */}
                    {loadingDeals ? (
                        <div className="bg-white rounded-2xl shadow-sm border border-orange-200 mb-8 overflow-hidden animate-pulse">
                            {/* Khung Header Deal */}
                            <div className="p-5 border-b border-gray-100">
                                <div className="flex justify-between items-center mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                                        <div className="h-6 w-48 bg-gray-200 rounded-md"></div>
                                    </div>
                                    <div className="h-8 w-32 bg-gray-200 rounded-full hidden md:block"></div>
                                </div>
                                <div className="h-10 w-32 bg-gray-200 rounded-md mt-6"></div>
                            </div>
                            {/* Khung Sản phẩm */}
                            <div className="p-4 md:p-6 bg-gray-50/50">
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                                    {[...Array(10)].map((_, i) => (
                                        <div key={i} className="bg-white h-[320px] rounded-xl border border-gray-200 shadow-sm flex flex-col p-3">
                                            <div className="w-full h-[160px] bg-gray-200 rounded-lg mb-4"></div>
                                            <div className="h-4 w-full bg-gray-200 rounded mb-2"></div>
                                            <div className="h-4 w-2/3 bg-gray-200 rounded mb-4"></div>
                                            <div className="h-6 w-1/2 bg-gray-200 rounded mt-auto"></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : dealGroups.length > 0 ? (
                        dealGroups.map((deal) => (
                            <SaleGroupSection 
                                key={deal.saleId}
                                sale={deal}
                                products={deal.items || []}
                                getImageUrl={getImageUrl}
                                adminSpecs={adminSpecs} 
                            />
                        ))
                    ) : (
                        <div className="py-24 flex flex-col items-center justify-center text-center bg-white rounded-2xl shadow-sm border border-gray-100">
                             <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                                <LayoutGrid className="text-gray-300 w-10 h-10" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-700 mb-2">Hiện chưa có Hot Deal nào!</h3>
                        </div>
                    )}

                    {/* 👉 TỐI ƯU 5: Tránh nhảy Footer bằng cách bọc trước skeleton cho phần Tin tức */}
                    {loadingNews ? (
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mt-12 animate-pulse">
                            <div className="flex items-center justify-between mb-6 border-b border-gray-100 pb-4">
                                <div className="h-6 w-48 bg-gray-200 rounded"></div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {[...Array(4)].map((_, i) => (
                                    <div key={i} className="h-48 bg-gray-200 rounded-xl border border-gray-100"></div>
                                ))}
                            </div>
                        </section>
                    ) : (newsList.length > 0 && (
                        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mt-12 [content-visibility:auto]">
                            <div className="flex items-center justify-between mb-6 border-b border-gray-100 pb-4">
                                <div className="flex items-center gap-2">
                                    <Newspaper className="text-tnc-orange" size={24} />
                                    <h2 className="text-xl font-bold text-gray-800 uppercase tracking-wide">Tin Tức Khuyến Mãi</h2>
                                </div>
                                <Link to="/tin-tuc" className="text-sm font-bold text-gray-500 hover:text-tnc-orange flex items-center gap-1 transition-colors">
                                    Xem thêm <ChevronRight size={16} />
                                </Link>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {newsList.map(news => (
                                    <Link to={`/tin-tuc/${generateSlug(news.newsTitle || 'tin-tuc')}-id${news.newsId}`} key={news.newsId} className="group border border-gray-100 rounded-xl overflow-hidden hover:shadow-md hover:border-orange-200 transition-all flex flex-col">
                                        <div className="aspect-video bg-gray-100 relative overflow-hidden">
                                            {/* Thêm loading lazy tránh delay LCP của ảnh Hot Deal */}
                                            <img 
                                                src={getImageUrl(news.newsImageUrl || news.newsImage)} 
                                                alt={news.newsTitle}
                                                loading="lazy"
                                                decoding="async"
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        </div>
                                        <div className="p-4 flex flex-col flex-1">
                                            <div className="text-[10px] font-bold text-gray-400 mb-2 flex items-center gap-1">
                                                <CalendarDays size={12} /> {new Date(news.newsCreatedTime || news.createdAt).toLocaleDateString('vi-VN')}
                                            </div>
                                            <h4 className="font-bold text-gray-800 text-sm line-clamp-2 group-hover:text-tnc-orange transition-colors leading-snug">
                                                {news.newsTitle}
                                            </h4>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default HotDealPage;